import React, { useEffect, useState, useMemo } from "react";
import styles from "./AdminPage.module.scss";
import { usePageTitle } from "../../contexts/PageTitleContext";
import { fetchClients } from "../../api/client";
import { fetchAppointments, fetchAppointmentsDetails } from "../../api/appointment";
import { fetchAvailableSlots } from "../../api/slot";
import { fetchTestimonialsAdmin } from "../../api/testimonial";
import { fetchPromotions } from "../../api/promotions";
import { fetchTopPages } from "../../api/stat";
import VisitsChart from "../../components/Matomo/VisitsChart/VisitsChart";
import KpiCard from "../../components/Dashboard/KpiCard/KpiCard";
import DashboardSection from "../../components/Dashboard/DashboardSection/DashboardSection";
import UpcomingAppointments from "../../components/Dashboard/UpcomingAppointments/UpcomingAppointments";
import GroupIcon from "@mui/icons-material/Group";
import EventIcon from "@mui/icons-material/Event";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

function AdminHome() {
  const { setPageTitle } = usePageTitle();
  const [clients, setClients] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [detailedAppointments, setDetailedAppointments] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageTitle("Tableau de bord");
  }, [setPageTitle]);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const [clientsRes, allApptsRes, detailedApptsRes, slotsRes, testimonialsRes, promotionsRes, topPagesRes] =
          await Promise.all([
            fetchClients().catch(() => ({ data: [] })),
            fetchAppointments().catch(() => ({ data: [] })),
            fetchAppointmentsDetails().catch(() => ({ data: [] })),
            fetchAvailableSlots().catch(() => ({ data: [] })),
            fetchTestimonialsAdmin().catch(() => ({ data: [] })),
            fetchPromotions().catch(() => ({ data: [] })),
            fetchTopPages().catch(() => ({ data: [] })),
          ]);

        setClients(clientsRes.data || []);
        setAllAppointments(allApptsRes.data || []);
        setDetailedAppointments(detailedApptsRes.data || []);
        setAvailableSlots(slotsRes.data || []);
        setTestimonials(testimonialsRes.data || []);
        setPromotions(promotionsRes.data || []);
        setTopPages(topPagesRes.data || []);
      } catch (error) {
        console.error("Erreur lors du chargement du dashboard :", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const kpis = useMemo(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

    const thisWeekAppointments = allAppointments.filter((rdv) => {
      const rdvDate = new Date(rdv.date);
      return rdvDate >= startOfWeek && rdvDate <= endOfWeek && rdv.status !== "cancelled";
    });

    const thisMonthRevenue = allAppointments
      .filter((rdv) => {
        const rdvDate = new Date(rdv.date);
        return rdvDate >= startOfMonth && rdvDate <= endOfMonth && rdv.status !== "cancelled";
      })
      .reduce((sum, rdv) => sum + (Number(rdv.total_amount) || 0), 0);

    return {
      totalClients: clients.length,
      weekAppointments: thisWeekAppointments.length,
      monthRevenue: thisMonthRevenue,
      availableSlots: availableSlots.length,
    };
  }, [clients, allAppointments, availableSlots]);

  const groupedDetailedAppointments = useMemo(() => {
    const map = {};
    detailedAppointments.forEach((row) => {
      const id = row.appointment_id;
      if (!map[id]) {
        map[id] = {
          id,
          firstname: row.firstname,
          lastname: row.lastname,
          date: row.date,
          start_time: row.start_time,
          end_time: row.end_time,
          status: row.status,
          prestations: [],
        };
      }
      if (row.prestation) {
        map[id].prestations.push({
          prestation: row.prestation,
          type: row.type_de_prestation,
        });
      }
    });
    return Object.values(map);
  }, [detailedAppointments]);

  const upcomingAppointments = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return groupedDetailedAppointments
      .filter((rdv) => {
        const rdvDate = new Date(rdv.date);
        return rdvDate >= now && rdv.status !== "cancelled";
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
  }, [groupedDetailedAppointments]);

  const pendingTestimonials = useMemo(() => {
    return testimonials.filter((t) => !t.is_processed);
  }, [testimonials]);

  const activePromotions = useMemo(() => {
    const now = new Date();
    return promotions.filter((p) => {
      const start = new Date(p.start);
      const end = new Date(p.end);
      return start <= now && now <= end;
    });
  }, [promotions]);

  if (loading) {
    return (
      <div className={styles.container}>
        <p className={styles.loading}>Chargement du tableau de bord...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.kpiGrid}>
        <KpiCard
          icon={<GroupIcon />}
          value={kpis.totalClients}
          label="Total clients"
        />
        <KpiCard
          icon={<EventIcon />}
          value={kpis.weekAppointments}
          label="RDV cette semaine"
        />
        <KpiCard
          icon={<AttachMoneyIcon />}
          value={`${kpis.monthRevenue.toFixed(0)} €`}
          label="Revenus du mois"
        />
        <KpiCard
          icon={<AccessTimeIcon />}
          value={kpis.availableSlots}
          label="Créneaux disponibles"
        />
      </div>

      <DashboardSection className={styles.chartSection}>
        <VisitsChart />
      </DashboardSection>

      <div className={styles.bottomGrid}>
        <DashboardSection title="Prochains rendez-vous">
          <UpcomingAppointments appointments={upcomingAppointments} />
        </DashboardSection>

        <DashboardSection title="Témoignages en attente">
          {pendingTestimonials.length === 0 ? (
            <p className={styles.emptyText}>Aucun témoignage en attente</p>
          ) : (
            <div className={styles.alertBox}>
              <span className={styles.alertCount}>{pendingTestimonials.length}</span>
              <span className={styles.alertLabel}>
                {pendingTestimonials.length === 1
                  ? "témoignage à modérer"
                  : "témoignages à modérer"}
              </span>
            </div>
          )}
        </DashboardSection>

        <DashboardSection title="Promotions actives">
          {activePromotions.length === 0 ? (
            <p className={styles.emptyText}>Aucune promotion en cours</p>
          ) : (
            <ul className={styles.promoList}>
              {activePromotions.map((promo) => (
                <li key={promo.id} className={styles.promoItem}>
                  <span className={styles.promoTitle}>{promo.title}</span>
                  <span className={styles.promoDates}>
                    {new Date(promo.start).toLocaleDateString("fr-FR")} —{" "}
                    {new Date(promo.end).toLocaleDateString("fr-FR")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </DashboardSection>

        <DashboardSection title="Pages les plus vues">
          {topPages.length === 0 ? (
            <p className={styles.emptyText}>Aucune donnée disponible</p>
          ) : (
            <ul className={styles.topPagesList}>
              {topPages.slice(0, 5).map((page, index) => (
                <li key={index} className={styles.topPageItem}>
                  <span className={styles.pageRank}>{index + 1}</span>
                  <span className={styles.pageUrl}>
                    {page.page_name
                      ? page.page_name.replace(/^https?:\/\/[^/]+/, "")
                      : "—"}
                  </span>
                  <span className={styles.pageViews}>{page.page_views || "—"}</span>
                </li>
              ))}
            </ul>
          )}
        </DashboardSection>
      </div>
    </div>
  );
}

export default AdminHome;
